import { IOperator } from 'api/types/_operator';
import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from 'common/toast';
import LabelWrapper from 'components/Common/LabelWrapper';
import ModalConfirm from 'components/Common/ModalConfirm';
import TooltipCustom from 'components/Common/TooltipCustom';
import { useRole } from 'components/Hooks/UserHooks';
import en from 'date-fns/locale/en-US';
import ko from 'date-fns/locale/ko';
import { ROLES_FOR_APP, isHavePermissionRole } from 'helpers/role';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import CountUp from 'react-countup';
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Card, CardBody, CardHeader, Col, Container, Row } from "reactstrap";
import { createSelector } from "reselect";
import { NumberParam, StringParam, useQueryParams, withDefault, } from "use-query-params";
import BreadCrumb from "../../../components/Common/BreadCrumb";
import TableContainer from "../../../components/Common/TableContainer";
import RuleForm from './RuleForm';
import { formatDateStringToKorea, formatNumberWithCommas } from 'helpers/format';
import CopyWrapper from 'components/Common/CopyWrapper';
import { deleteReferralRule, getReferralRules, putReferralRule } from 'store/referral-rule/thunk';
import DropdownStatusNew from 'components/Common/DropdownStatusNew';
import { COLOR_REFERRAL_RULE_ROLE, COLOR_STATUS_ACTIVE_DISABLE, COLOR_STATUS_POST, getPlatFormImage, LIST_REFERRAL_RULE_ROLE_OPTION, STATUS_ACTIVE_DISABLE_OPTION, STATUS_POST_OPTION } from 'helpers/constans';
import DatePickerCustom from 'components/Common/DatePickerCustom';
import moment from 'moment';
import DropdownOptionNew from 'components/Common/DropdownOptionNew';
import { Option } from 'api/types/_public';


registerLocale('en', en);
registerLocale('ko', ko);

const ReferralRules = () => {
  const { t, i18n } = useTranslation();
  const { userPermissions } = useRole();
  const navigate = useNavigate();

  const LEVELS = Array.from({ length: 10 }, (_, index) => ({
    value: index + 1,
    label: `${t('Level')} ${index + 1}`
  }));

  const [query, setQuery]: any = useQueryParams({
    time_request: withDefault(NumberParam, 0),
    page: withDefault(NumberParam, 1),
    limit: withDefault(NumberParam, 30),
    from_role: withDefault(StringParam, ""),
    to_role: withDefault(StringParam, ""),
    level: withDefault(StringParam, null),
    trigger_event_code: withDefault(StringParam, ""),
    status: withDefault(StringParam, ""),
    start_date: withDefault(StringParam, ""),
    end_date: withDefault(StringParam, ""),
    sort_by: withDefault(StringParam, ''),
    keyword: withDefault(StringParam, ''),
  });

  const [levelSearch, setLevelSearch] = useState<string>(query?.level || "");
  const [fromRoleSearch, setFromRoleSearch] = useState<string>(query?.from_role || "");
  const [toRoleSearch, setToRoleSearch] = useState<string>(query?.to_role || "");

  const [statusSearch, setStatusSearch] = useState<Option | any>(
    STATUS_ACTIVE_DISABLE_OPTION?.find(
      (item) => item?.value === query?.status
    )?.value || ""
  );

  const [dateSearch, setDateSearch] = useState<any[]>([
    query?.start_date
      ? moment(query?.start_date || "", "Y-MM-DD").toDate()
      : null,
    query?.end_date ? moment(query?.end_date || "", "Y-MM-DD").toDate() : null,
  ]);
  const [startDate, endDate] = dateSearch;

  const [ruleEdit, setRuleEdit] = useState<any>(null);

  const [isOpenConfirm, setIsConfirm] = useState<boolean>(false);
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  const [initialValuesDefault, setInitialValuesDefault] = useState<IOperator | null>(null);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const dispatch: any = useDispatch();

  const selectLayoutState = (state: any) => state.Rule;

  const RuleProperties = createSelector(
    selectLayoutState,
    (state) => ({
      rules: state.rules,
      isRuleSuccess: state.isRuleSuccess,
      isRuleLoading: state.isRuleLoading,
      error: state.error,
    })
  );

  // Inside your component
  const { rules, error, isRuleLoading } = useSelector(RuleProperties);


  useEffect(() => {
    dispatch(getReferralRules(query));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, JSON.stringify(query)]);

  const searchData = () => {
    setQuery({
      ...query,
      from_role: fromRoleSearch,
      to_role: toRoleSearch,
      level: levelSearch,
      status: statusSearch,
      start_date: startDate
        ? moment(new Date(startDate)).format("Y-MM-DD")
        : "",
      end_date: endDate
        ? moment(new Date(endDate)).endOf("day").format("Y-MM-DD")
        : "",
      
      page: 1,
      time_request: + new Date()
    });
  };

  const resetData = () => {
    setQuery({
      keyword: '',
      from_role: "",
      to_role: "",
      level: null,
      start_date: "",
      end_date: "",
      sort_by: '',
      time_request: + new Date(),
      page: 1,
    }, "push")
    setLevelSearch((_prev) => "");
    setStatusSearch("");
    setFromRoleSearch((_prev) => "");
    setToRoleSearch((_prev) => "");
    setDateSearch([null, null]);
  };

  const handleChangePicker = (values: any[] = []) => {
    setDateSearch((_prev: any) => values);
  };

  // Begin::Update User
  const onCloseClick = () => {
    setRuleEdit(null);
    setIsOpen((_prev) => false)
  }

  const triggerRefresh = () => {
    onCloseClick();
    dispatch(getReferralRules(query));
  }
  //End::Update User

  // Begin::Delete
  const handleConfirmDelete = (item: IOperator) => {
    setInitialValuesDefault((_prev) => item);
    setIsConfirm((_prev) => true);
  };

  const handleActionDelete = async () => {
    if (!(initialValuesDefault?.id)) { return; };
    try {
      setIsConfirmLoading((_prev) => true);
      const response: any = await deleteReferralRule(initialValuesDefault?.id);
      if (response?.code === 200) {
        dispatch(getReferralRules(query));
        setIsConfirmLoading((_prev) => false);
        setIsConfirm((_prev) => false);
        setInitialValuesDefault((_prev) => null);
        toast(`${t('The process has been completed.')}`, CONFIG_OPTION_TOAST_NORMAL);
      } else {
        setIsConfirmLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsConfirmLoading((_prev) => false);
      toast(`${error?.message || ''}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const onCloseConfirmClick = () => {
    setInitialValuesDefault((_prev) => null);
    setIsConfirm((_prev) => false);
  }
  // End::Delete

  // Column
  const columns = useMemo(
    () => [
      {
        Header: t("No."),
        thWidth: 80,
        thClass: "align-middle",
        Cell: ({ ...props }) => {
          const item = props?.row?.original;
          return (
            <CopyWrapper contentCopy={item?.id || ''} style={{ display: 'inline-flex', minWidth: '50px' }}>
              <div>
                {rules?.pagination?.total -
                  ((query?.page - 1) * query?.limit + props?.row?.index)}
              </div>
            </CopyWrapper>
          );
        },
      },
      {
        Header: t('Status'),
        accessor: "status",
        filterable: false,
        sortable: false,
        thClass: 'text-center',
        thWidth: 100,
        Cell: (cell: any) => {
          const value: any = cell?.row?.original;
          const active = value?.status;
          return (
            <div className="text-center form-check form-switch form-switch-sm" dir="ltr" style={{ minWidth: '50px' }}>
              <input type="checkbox" className="form-check-input" name="is_active" id="customSwitchsizelg" checked={Boolean(active)}
                onChange={(event: any) => {
                  if (isHavePermissionRole(ROLES_FOR_APP.OPERATOR_UPDATE, userPermissions)) {
                    handleChangeStatusItem(value, event.target.checked)
                  }
                }}
              />
            </div>
          );
        }
      },
      {
        Header: t('From Role'),
        accessor: "from_role",
        thClass: "text-center",
        filterable: true,
        sortable: false,
        Cell: (cell: any) => {
          return (
            <div className="text-center" style={{ minWidth: 80 }}>
              <span
                className={`rounded-pill badge bg-${COLOR_REFERRAL_RULE_ROLE[cell?.value] || "secondary"
                  }`}
              >
                {t(
                  LIST_REFERRAL_RULE_ROLE_OPTION?.find(
                    (item: any) => item?.value === cell?.value
                  )?.label || ""
                )}
              </span>
            </div>
          );
        },
      },
      {
        Header: t('To Role'),
        accessor: "to_role",
        thClass: "text-center",
        filterable: true,
        sortable: false,
        Cell: (cell: any) => {
          return (
            <div className="text-center" style={{ minWidth: 80 }}>
              <span
                className={`rounded-pill badge bg-${COLOR_REFERRAL_RULE_ROLE[cell?.value] || "secondary"
                  }`}
              >
                {t(
                  LIST_REFERRAL_RULE_ROLE_OPTION?.find(
                    (item: any) => item?.value === cell?.value
                  )?.label || ""
                )}
              </span>
            </div>
          );
        },
      },
      {
        Header: t('Level'),
        accessor: "level",
        filterable: true,
        sortable: false,
        thWidth: 100,
        Cell: (cell: any) => {
          const item = cell?.row?.original;
          return (<>
            <div style={{ minWidth: '80px' }}>{LEVELS?.find((item) => (item?.value === cell?.value))?.label || ''}</div>
          </>)
        },
      },
      {
        Header: t('Amount'),
        accessor: "amount",
        thClass: "text-end",
        thWidth: 150,
        filterable: true,
        sortable: false,
        Cell: (cell: any) => {
          const item = cell?.row?.original;
          return (<>
            <div className="text-end" style={{ minWidth: '100px' }}>
              <div>{ formatNumberWithCommas(cell?.value || 0) }</div>
            </div>
          </>)
        },
      },
      {
        Header: `${t("Currency")}`,
        accessor: "currency",
        filterable: false,
        sortable: false,
        thWidth: 140,
        thClass: "text-center whitespace-nowrap",
        Cell: (cell: any) => {
          let imgPlatform = getPlatFormImage(cell?.value ?? '');

          return cell?.value && (
            <div style={{ textAlign: "center" }}>
              {imgPlatform && (
                <img
                  src={imgPlatform}
                  width={25}
                  height={25}
                  alt="snsType"
                />
              )}
            </div>
          );
        },
      },
      {
        Header: t('Reward Distribution Conditions'),
        accessor: "trigger_event",
        filterable: true,
        sortable: false,
        Cell: (cell: any) => {
          const item = cell?.value;
          return item?.name ? (<>
            <div>{`${item?.name} ${item?.valid_within_hours ? ` - ${t('Valid')} ` + item?.valid_within_hours + ` ${t('hours')}`  : ''}`}</div>
          </>) : '-'
        },
      },
      {
        Header: t('Created at'),
        accessor: "created_at",
        filterable: true,
        sortable: false,
        thWidth: 110,
        thClass: 'text-end',
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value || '');
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: '100px' }}>
              <span><span>{arrDate?.[0] || ''}</span> <br /> <span className="text-secondary">{arrDate?.[1] || ''}</span> </span>
            </div>
          )
        },
      },
      {
        Header: t('Updated at'),
        accessor: "updated_at",
        filterable: true,
        sortable: false,
        thWidth: 110,
        thClass: 'text-end',
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value || '');
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: '100px' }}>
              <span><span>{arrDate?.[0] || ''}</span> <br /> <span className="text-secondary">{arrDate?.[1] || ''}</span> </span>
            </div>
          )
        },
      },
      {
        Header: t('Action'),
        thClass: 'text-center',
        thWidth: 100,
        Cell: (cell: any) => {
          const item: any = cell?.row?.original;
          return (
            <ul className="list-inline flex-wrap justify-content-center hstack gap-2 mb-0" style={{ minWidth: '70px' }}>
              {isHavePermissionRole(ROLES_FOR_APP.OPERATOR_UPDATE, userPermissions) && (
                <TooltipCustom
                  title={t('Button Update Invitation Rule')}
                  id={`update-us-${item?.id}`}
                >
                  <Link className="btn btn-sm btn-soft-primary edit-item-btn" to="#"
                    onClick={(e) => { 
                      setIsOpen(true);
                      setRuleEdit(item)
                    }}
                  >
                    <i className="ri-pencil-fill align-bottom"></i>
                  </Link>
                </TooltipCustom>
              )}
              {isHavePermissionRole(ROLES_FOR_APP.OPERATOR_DELETE, userPermissions) && (
                <TooltipCustom
                  title={t('Button Delete Invitation Rule')}
                  id={`detail-cp-${item?.id}`}
                >
                    <Link className="btn btn-sm btn-soft-danger edit-item-btn" to="#"
                      onClick={(e) => { e.preventDefault(); handleConfirmDelete(item) }}
                    >
                      <i className="ri-delete-bin-3-fill align-bottom"></i>
                    </Link>
                </TooltipCustom>
              )}
            </ul>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n?.language, query, userPermissions, rules?.pagination?.total]
  );

  const handleChangePage = useCallback((page: any) => {
    setQuery({ page: page + 1 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeSorting = useCallback((sortBy: any) => {
    setQuery((_prev: any) => {
      return ({ ..._prev, ...sortBy });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeStatusItem = async (item: any, status: boolean) => {
    try {
      const { email, fist_name, last_name, role_id } = structuredClone(item);
      const data = {
        // email,
        // fist_name,
        // last_name,
        // role_id,
        status: Number(status),
      };
      const response: any = await putReferralRule(item?.id, data);
      if (response?.data) {
        dispatch(getReferralRules(query));
        toast(`${t('The process has been completed.')}`, CONFIG_OPTION_TOAST_NORMAL);
      } else {
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      toast(`${error?.message || ''}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  }


  useEffect(() => {
    document.title = `${t('Invitation Rules')} - ${t('Home')} | Baive`;
    document.body.classList.remove('vertical-sidebar-enable');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t('Invitation Rules')} pageTitle={t('Home')} />
          <Row>
            {isHavePermissionRole(ROLES_FOR_APP.OPERATOR_LIST, userPermissions) && (
              <Col lg={12}>
                <Card id="customerList" style={{ boxShadow: '0px 3px 4px 0px rgba(0, 0, 0, 0.03)' }}>
                  <CardHeader className="border-0">
                    <Row className="g-4 align-items-center">
                      <div className="col-5 con-sm-6">
                        <div>
                          <h5 className="card-title mb-0">
                            <span className="me-2">{t('Total')}:</span>
                            <CountUp
                              start={0}
                              end={rules?.pagination?.total || 0}
                              duration={1}
                              className="text-primary"
                            />
                          </h5>
                        </div>
                      </div>
                      <div className="col-7 con-sm-6 text-end">
                        {isHavePermissionRole(ROLES_FOR_APP.OPERATOR_CREATE, userPermissions) && (<div className="col-sm-auto">
                          <div>
                            <button
                              type="button"
                              className="btn btn-success add-btn"
                              id="create-btn"
                              onClick={() => setIsOpen((_prev) => true)}
                            >
                              <i className="ri-add-line align-bottom me-1"></i>
                              {t('Button Create Invitation Rule')}
                            </button>
                          </div>
                        </div>)}
                      </div>
                    </Row>
                  </CardHeader>
                  <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-4">
                    <Row className="g-4 align-items-center mt-0">
                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-2"
                      >
                        <LabelWrapper label={t("From Role")} isShow={true}>
                          <DropdownStatusNew
                            name="from-role"
                            dataList={LIST_REFERRAL_RULE_ROLE_OPTION || []}
                            placeholder={`${t("Select Role")}...`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={fromRoleSearch}
                            onChangeSelect={(e: any) =>
                              setFromRoleSearch(e?.value)
                            }
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                            colors={COLOR_REFERRAL_RULE_ROLE}
                          />
                        </LabelWrapper>
                      </Col>
                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-2"
                      >
                        <LabelWrapper label={t("To Role")} isShow={true}>
                          <DropdownStatusNew
                            name="to-role"
                            dataList={LIST_REFERRAL_RULE_ROLE_OPTION || []}
                            placeholder={`${t("Select Role")}...`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={toRoleSearch}
                            onChangeSelect={(e: any) =>
                              setToRoleSearch(e?.value)
                            }
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                            colors={COLOR_REFERRAL_RULE_ROLE}
                          />
                        </LabelWrapper>
                      </Col>
                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-2"
                      >
                        <LabelWrapper label={t("Level")} isShow={true}>
                          <DropdownOptionNew
                            name="app_name"
                            dataList={LEVELS || []}
                            placeholder={`${t("Select level")}...`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={levelSearch}
                            onChangeSelect={(e: any) =>
                              setLevelSearch(e?.value)
                            }
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                            hideSelectedOptions={false}
                          />
                        </LabelWrapper>
                      </Col>
                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-2"
                      >
                        <LabelWrapper label={t("Status")} isShow={true}>
                          <DropdownStatusNew
                            name="status"
                            dataList={STATUS_POST_OPTION || []}
                            placeholder={`${t("Select Status")}...`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={statusSearch}
                            onChangeSelect={(e: any) =>
                              setStatusSearch(e?.value)
                            }
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                            colors={COLOR_STATUS_POST}
                          />
                        </LabelWrapper>
                      </Col>
                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-sm-4 mt-md-3 mt-3 date-picker-wrapper-custom"
                      >
                        <LabelWrapper
                          label={t("Date")}
                          isShow={!!startDate || !!endDate}
                        >
                          <DatePickerCustom
                            startDate={startDate || null}
                            endDate={endDate || null}
                            onChangePicker={handleChangePicker}
                          />
                        </LabelWrapper>
                      </Col>
                      <Col sm={12} md={9} xl={9} xxl={9} className="hstack gap-1 justify-content-center justify-content-sm-end mt-3 mt-md-3">
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary me-2 "
                            onClick={searchData}
                            disabled={isRuleLoading}
                          >
                            <i className="ri-search-line align-bottom me-1"></i>{" "}
                            {t('Button Search')}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary fs-14"
                            onClick={resetData}
                          >
                            <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                            {t('Button Reset')}
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </div>
                  <CardBody className="pt-3">
                    <TableContainer
                      className="custom-header-css"
                      divClass="table-responsive table-card"
                      tableClass="align-middle"
                      theadClass="table-light text-muted"
                      columns={columns}
                      data={rules?.items?.length ? rules?.items : []}
                      customPageSize={query.limit}
                      customPageIndex={query.page - 1}
                      totalRecords={rules?.pagination?.total}
                      customPageCount={Math.ceil(Number(rules?.pagination?.total) / Number(rules?.pagination?.limit))}
                      handleChangePage={handleChangePage}
                      manualSorting={true}
                      sorting={{ sort_by: query.sort_by, order_by: query.order_by }}
                      handleChangeSorting={handleChangeSorting}
                      isLoading={isRuleLoading}
                    />
                  </CardBody>
                </Card>
              </Col>
            )}</Row>
        </Container>
        <ToastContainer closeButton={false} newestOnTop={false} pauseOnFocusLoss autoClose={2000} limit={2} />

        {
          isOpen && (
            <RuleForm
                id={String(ruleEdit?.id || '')}
                item={ruleEdit}
                triggerRefresh={triggerRefresh}
                lstLevel={LEVELS}
                onCloseClick={onCloseClick}
              />
          )
        }

        <ModalConfirm
          header={t('Button Delete Invitation Rule')}
          isOpen={isOpenConfirm}
          isLoading={isConfirmLoading}
          onClose={onCloseConfirmClick}
          onConfirm={handleActionDelete}
        />
      </div >
    </React.Fragment >
  );
};

export default ReferralRules;